import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Spinner, Toast, ToastContainer } from "react-bootstrap";
import Loader from "../common/Loader.jsx";
import ReactPaginate from "react-paginate";
import DOMPurify from "dompurify";
import RecordsPerPage from "../common/RecordsPerPage.jsx";
import lar from "../../assets/svg/lar.svg";
import rar from "../../assets/svg/rar.svg";

// Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canEdit, canDelete, canCreate } from "../common/permissionUtils";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import "./account-view.css";
import Back from "../../assets/svg/back.svg";
import Search from "../../assets/svg/search.svg";
import UserIcon from "../../assets/svg/user.svg";
import MailIcon from "../../assets/svg/mail.svg";
import PhoneIcon from "../../assets/svg/phone.svg";
import BuildingIcon from "../../assets/svg/building.svg";
import amountIcon from "../../assets/svg/amount.svg";
import ITIcon from "../../assets/svg/it.svg";
import LocationIcon from "../../assets/svg/location.svg";
import Clock from "../../assets/svg/notesclock.svg";
import Notesprofile from "../../assets/svg/notesprofile.svg";
import ArrowPrev from "../../assets/svg/arrow-prev.svg";
import ArrowNext from "../../assets/svg/arrow-next.svg";
import { FaPlus, FaDownload } from "react-icons/fa";
import { 
  getAccountViewApi, 
  getAccountContactsApi, 
  getAccountQuotationsApi, 
  getAccountInvoicesApi, 
  getAccountPaymentsApi,
  getAccountTransactionsApi,
  getAccountNotesApi,
  saveAccountNoteApi, 
  getAccountAttachmentsApi, 
  saveAccountAttachmentApi,
  getAccountMailTemplateApi,
  sendAccountMailApi,
  updateAccountStatusApi,
  getAccountDiscussionApi,
  getAccountMailDetailApi
} from "./accounts-api";
import OutstandingReportModal from "./outstanding-report-modal";
import CreateContactModal from "../common/CreateContactModal.jsx";
import CreateCallModal from "../common/CreateCallModal.jsx";
import DeleteModal from "../common/delete-modal.js";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";
import CloseIcon from "../../assets/svg/close-modal.svg";
import AttachIcon from "../../assets/svg/upload-icon.svg";
import LinkIcon from "../../assets/svg/link_icon.svg";
import ClockIcon from "../../assets/svg/notesclock.svg";
import EditPenIcon from "../../assets/svg/edit_ic.svg";
import CameraIcon from "../../assets/svg/camera.svg";
import AiSummaryCard from "../common/ai-summary-card.js";
import { getAccountAiSummaryApi } from "./accounts-api";

// Shared page-per-page pagination footer for the Contacts/Quotation/Invoice/
// Payment History/Transaction History tabs on this page. Mirrors the
// RecordsPerPage + ReactPaginate pattern used in account-list.js.
const PaginationControls = ({ state, onItemsPerPageChange, onPageChange }) => {
  if (state.totalRecords === 0) return null;
  return (
    <div className="d-flex align-items-center justify-content-between" style={{ padding: "12px 4px 0" }}>
      <RecordsPerPage
        value={state.itemsPerPage}
        onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
      />
      <ReactPaginate
        key={state.offset}
        previousLabel={<img src={lar} className="ar3" alt="previous" />}
        nextLabel={<img src={rar} className="ar3" alt="next" />}
        breakLabel={"..."}
        pageCount={state.pageCount + 1}
        marginPagesDisplayed={2}
        pageRangeDisplayed={3}
        onPageChange={(event) => onPageChange(event.selected)}
        forcePage={state.offset}
        containerClassName={"pagination"}
        activeClassName={"active"}
      />
    </div>
  );
};

const AccountView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const accountId = location.state?.id;

  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasEditPermission = canEdit(rolePermissionList, "Accounts");
  const hasDeletePermission = canDelete(rolePermissionList, "Accounts");
  const hasCreateCallPermission = canCreate(rolePermissionList, "Calls");

  const [activeTab, setActiveTab] = useState("Account Details");
  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [history, setHistory] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [discussionMails, setDiscussionMails] = useState([]);
  const [selectedMail, setSelectedMail] = useState(null);
  const [mailDetailLoading, setMailDetailLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState({});
  const [contactSearch, setContactSearch] = useState("");
  const [quotationSearch, setQuotationSearch] = useState("");
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [paymentSearch, setPaymentSearch] = useState("");
  const [transactionSearch, setTransactionSearch] = useState("");

  // Per-page-per-page pagination for the Contacts/Quotation/Invoice/Payment
  // History/Transaction History tabs — each tab keeps its own page position
  // and page size, keyed by tab name, since only one tab is visible at a
  // time but switching back to a previously-viewed tab should keep its page.
  const PAGINATED_TABS = ["Contacts", "Quotation", "Invoice", "Payment History", "Transaction History"];
  const defaultPagination = { offset: 0, itemsPerPage: 10, pageCount: 0, totalRecords: 0 };
  const [pagination, setPagination] = useState(() =>
    Object.fromEntries(PAGINATED_TABS.map((tab) => [tab, { ...defaultPagination }])),
  );

  const setTabPageOffset = (tab, offset) => {
    setPagination((prev) => ({ ...prev, [tab]: { ...prev[tab], offset } }));
  };

  const setTabItemsPerPage = (tab, itemsPerPage) => {
    setPagination((prev) => ({ ...prev, [tab]: { ...prev[tab], itemsPerPage, offset: 0 } }));
  };

  // fetchTabData reads pagination via this ref (kept in sync below) instead
  // of depending on the `pagination` state object directly. applyPaginationResult
  // updates `pagination` on every successful fetch, so making fetchTabData's
  // useCallback depend on `pagination` would change its identity on every
  // fetch, which retriggers the tab-switch effect below it — an infinite
  // refetch loop, and the root cause of every list API firing repeatedly.
  const paginationRef = useRef(pagination);
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);
  const fileInputRef = useRef(null);
  const [showMailModal, setShowMailModal] = useState(false);
  const [mailLoading, setMailLoading] = useState(false);
  const [mailData, setMailData] = useState({
    to_email: "",
    cc_emails: "",
    bcc_emails: "",
    subject: "",
    body: "",
  });
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [ccChips, setCcChips] = useState([]);
  const [bccChips, setBccChips] = useState([]);
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");
  const [mailError, setMailError] = useState("");
  const [isFormatting, setIsFormatting] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showKebabMenu, setShowKebabMenu] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showDriveInput, setShowDriveInput] = useState(false);
  const [driveLink, setDriveLink] = useState("");
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkDisplay, setLinkDisplay] = useState("");
  const mailAttachmentRef = useRef(null);
  const [sidebarContact, setSidebarContact] = useState(null);
  const tabListRef = useRef(null);

  const [showOutstandingReport, setShowOutstandingReport] = useState(
    Boolean(location.state?.openScheduleModal)
  );
  const [initialReportModalTab, setInitialReportModalTab] = useState(
    location.state?.initialModalTab || (location.state?.openScheduleModal ? "Schedule" : "Preview")
  );

  useEffect(() => {
    if (location.state?.openScheduleModal) {
      setInitialReportModalTab(location.state?.initialModalTab || "Schedule");
      setShowOutstandingReport(true);
    }
  }, [location.state]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSuccess, setToastSuccess] = useState(true);

  // Create Call popup (shared component)
  const [showCreateCall, setShowCreateCall] = useState(false);

  // Create Contact popup (shared component)
  const [showCreateContact, setShowCreateContact] = useState(false);

  // Delete Contact
  const [deleteContactId, setDeleteContactId] = useState(null);
  const [isOpenDeleteContact, setIsOpenDeleteContact] = useState(false);

  const notify = (msg, isSuccess = true) => {
    setToastMessage(msg);
    setToastSuccess(isSuccess);
    setShowToast(true);
  };

  const scrollTabs = (direction) => {
    if (tabListRef.current) {
      tabListRef.current.scrollBy({ left: direction * 150, behavior: "smooth" });
    }
  };

  const tabs = ["Account Details", "AI Summary", "Contacts", "Quotation", "Invoice", "Payment History", "Transaction History", "Notes", "Attachments", "Discussion"];

  const fetchAccountDetails = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const response = await getAccountViewApi(accountId);
      if (response?.data?.success) {
        setAccountData(response.data.account);
        
        // Fetch first contact for sidebar
        const contactResponse = await getAccountContactsApi(accountId, "?limit=1");
        if (contactResponse?.data?.success && contactResponse.data.contacts?.length > 0) {
          setSidebarContact(contactResponse.data.contacts[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching account details:", error);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  // Builds "?search=...&limit=...&skip=..." for a paginated tab, reusing
  // whatever page/page-size that tab is currently on. Reads from the ref
  // (not the `pagination` state directly) so this can be called from
  // fetchTabData without making fetchTabData's identity depend on pagination.
  const buildPaginatedParams = (search, tab) => {
    const { offset, itemsPerPage } = paginationRef.current[tab];
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    params.set("limit", itemsPerPage);
    params.set("skip", offset * itemsPerPage);
    return `?${params.toString()}`;
  };

  // Applies a paginated tab's total_rec/total_pgs from the API response onto
  // that tab's pagination state (pageCount mirrors account-list.js: total_pgs
  // is already zero-indexed max-page, ReactPaginate is given pageCount + 1).
  const applyPaginationResult = (tab, data) => {
    setPagination((prev) => ({
      ...prev,
      [tab]: { ...prev[tab], totalRecords: data?.total_rec ?? 0, pageCount: data?.total_pgs ?? 0 },
    }));
  };

  const fetchTabData = useCallback(
    async (tab) => {
      if (!accountId) return;
      setTabLoading((prev) => ({ ...prev, [tab]: true }));
      try {
        let response;
        switch (tab) {
          case "Contacts":
            response = await getAccountContactsApi(accountId, buildPaginatedParams(contactSearch, tab));
            if (response?.data?.success) {
              setContacts(response.data.contacts);
              applyPaginationResult(tab, response.data);
            }
            break;
          case "Quotation":
            response = await getAccountQuotationsApi(accountId, buildPaginatedParams(quotationSearch, tab));
            if (response?.data?.success) {
              setQuotations(response.data.quotations);
              applyPaginationResult(tab, response.data);
            }
            break;
          case "Invoice":
            response = await getAccountInvoicesApi(accountId, buildPaginatedParams(invoiceSearch, tab));
            if (response?.data?.success) {
              setInvoices(response.data.invoices);
              applyPaginationResult(tab, response.data);
            }
            break;
          case "Payment History":
            response = await getAccountPaymentsApi(accountId, buildPaginatedParams(paymentSearch, tab));
            if (response?.data?.success) {
              setPayments(response.data.payments);
              applyPaginationResult(tab, response.data);
            }
            break;
          case "Transaction History":
            response = await getAccountTransactionsApi(accountId, buildPaginatedParams(transactionSearch, tab));
            if (response?.data?.success) {
              setTransactions(response.data.transactions);
              applyPaginationResult(tab, response.data);
            }
            break;
          case "Notes":
            response = await getAccountNotesApi(accountId, "");
            if (response?.data?.success) setNotes(response.data.notes);
            break;
          case "Attachments":
            response = await getAccountAttachmentsApi(accountId, "");
            if (response?.data?.success) setAttachments(response.data.attachments);
            break;
          case "Discussion":
            response = await getAccountDiscussionApi(accountId, "");
            if (response?.data?.success) setDiscussionMails(response.data.mails);
            break;
          default:
            break;
        }
      } catch (error) {
        console.error(`Error fetching ${tab}:`, error);
      } finally {
        setTabLoading((prev) => ({ ...prev, [tab]: false }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountId, contactSearch, quotationSearch, invoiceSearch, paymentSearch, transactionSearch],
  );

  useEffect(() => {
    fetchAccountDetails();
  }, [fetchAccountDetails]);

  const handleContactCreated = () => {
    if (activeTab === "Contacts") fetchTabData("Contacts");
  };

  const handleDeleteContact = (contactId) => {
    if (!hasDeletePermission) {
      notify("You don't have permission to delete contacts", false);
      return;
    }
    setDeleteContactId(contactId);
    setIsOpenDeleteContact(true);
  };

  const cancelDeleteContact = () => {
    setIsOpenDeleteContact(false);
    setDeleteContactId(null);
  };

  const onSuccessDeleteContact = (response) => {
    setIsOpenDeleteContact(false);
    setDeleteContactId(null);
    notify(
      response?.message ||
        (response?.success
          ? response?.unmapped
            ? "Contact unmapped from account successfully"
            : "Contact deleted successfully"
          : "Failed to delete contact"),
      response?.success
    );
    if (response?.success) fetchTabData("Contacts");
  };

  const handleOpenMailModal = async () => {
    try {
      const response = await getAccountMailTemplateApi(accountId);
      if (response?.data?.success) {
        setMailData({
          to_email: response.data.data.to_email || "",
          cc_emails: "",
          bcc_emails: "",
          subject: response.data.data.subject || "",
          body: "",
        });
        setShowCc(false);
        setShowBcc(false);
        setCcChips([]);
        setBccChips([]);
        setCcInput("");
        setBccInput("");
        setIsFormatting(false);
        setSelectedFiles([]);
        setShowEmojiPicker(false);
        setShowDriveInput(false);
        setShowLinkInput(false);
        setLinkUrl("");
        setLinkDisplay("");
        setShowMailModal(true);
      }
    } catch (error) {
      console.error("Error fetching mail template:", error);
    }
  };

  const isValidEmail = (email) => {
    return /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email.trim());
  };

  const hasCcInvalidInput = ccInput.trim() !== "" && !isValidEmail(ccInput.trim());
  const hasBccInvalidInput = bccInput.trim() !== "" && !isValidEmail(bccInput.trim());

  const handleSendMail = async () => {
    setMailError("");
    if (!mailData.to_email || !mailData.subject || !mailData.body) {
      setMailError("Please fill all required fields (To, Subject, Body)");
      return;
    }

    if (!isValidEmail(mailData.to_email)) {
      setMailError("Please enter a valid email address for 'To'");
      return;
    }

    // Validate pending CC/BCC input text
    if (ccInput.trim() !== "") {
      if (!isValidEmail(ccInput.trim())) {
        setMailError(`Invalid CC email: ${ccInput.trim()}`);
        return;
      }
      setCcChips([...ccChips, ccInput.trim()]);
      setCcInput("");
    }

    if (bccInput.trim() !== "") {
      if (!isValidEmail(bccInput.trim())) {
        setMailError(`Invalid BCC email: ${bccInput.trim()}`);
        return;
      }
      setBccChips([...bccChips, bccInput.trim()]);
      setBccInput("");
    }

    const invalidCc = ccChips.find(email => !isValidEmail(email));
    if (invalidCc) {
      setMailError(`Invalid CC email: ${invalidCc}`);
      return;
    }

    const invalidBcc = bccChips.find(email => !isValidEmail(email));
    if (invalidBcc) {
      setMailError(`Invalid BCC email: ${invalidBcc}`);
      return;
    }

    setMailLoading(true);
    try {
      const attachments = await Promise.all(
        selectedFiles.map(
          (file) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve({ file_name: file.name, file: reader.result });
              reader.onerror = reject;
              reader.readAsDataURL(file);
            }),
        ),
      );

      // TEMPORARY: merge CC into the To recipients at the API-call boundary
      // only — UI state (mailData.to_email/ccChips) is untouched, this just
      // reshapes the outgoing payload. cc_emails is sent empty so nothing is
      // duplicated as a separate CC on the backend.
      const mergedToEmail = [mailData.to_email, ...ccChips].filter(Boolean).join(",");
      const payload = {
        ...mailData,
        to_email: mergedToEmail,
        cc_emails: [],
        bcc_emails: bccChips,
        attachments,
      };
      const response = await sendAccountMailApi(accountId, payload);
      if (response?.data?.success) {
        setShowMailModal(false);
        notify(response.data.message || "Email sent successfully", true);
        if (activeTab === "Discussion") {
          fetchTabData("Discussion");
        }
      } else {
        notify(response?.data?.message || "Failed to send email", false);
      }
    } catch (error) {
      console.error("Error sending mail:", error);
      notify(error?.response?.data?.message || "Failed to send email", false);
    } finally {
      setMailLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      const response = await updateAccountStatusApi(accountId, newStatus);
      if (response?.data?.success) {
        setAccountData({ ...accountData, status: newStatus });
        setShowStatusDropdown(false);
      }
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  useEffect(() => {
    // AI Summary fetches its own data through the card, and there is no
    // server-side tab endpoint of that name to call.
    if (activeTab !== "Account Details" && activeTab !== "AI Summary") {
      fetchTabData(activeTab);
    }
  }, [activeTab, fetchTabData]);

  // Refetch the active tab's list when its page or page size changes (the
  // tab-switch effect above already covers the initial load for a tab, so
  // this only needs to fire on subsequent page/size changes — skipped on the
  // render right after activeTab itself changes to avoid double-fetching).
  const activeTabPagination = PAGINATED_TABS.includes(activeTab) ? pagination[activeTab] : null;
  const prevActiveTabRef = useRef(activeTab);
  useEffect(() => {
    if (!activeTabPagination) return;
    if (prevActiveTabRef.current !== activeTab) {
      prevActiveTabRef.current = activeTab;
      return;
    }
    fetchTabData(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabPagination?.offset, activeTabPagination?.itemsPerPage]);

  const handleAddNote = async () => {
    if (newNote.trim()) {
      try {
        const response = await saveAccountNoteApi(accountId, { note: newNote });
        if (response?.data?.success) {
          setNewNote("");
          fetchTabData("Notes");
        }
      } catch (error) {
        console.error("Error saving note:", error);
      }
    }
  };

  const handleDownload = (url, fileName) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAddAttachment = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64String = reader.result;
        try {
          const response = await saveAccountAttachmentApi(accountId, {
            attachment: base64String,
          });
          if (response?.data?.success) {
            fetchTabData("Attachments");
          }
        } catch (error) {
          console.error("Error saving attachment:", error);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const renderAccountDetails = () => (
    <>
      <div className="view-card">
        <div className="view-card-title">Account details</div>
        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-label">Account Site</span>
            <span className="detail-value">{accountData?.account_site || "Chennai"}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Parent Account</span>
            <span className="detail-value" style={{ color: "#6B46FE" }}>
              {accountData?.parent_account || "Lentera Technologies"}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Account Type</span>
            <span className="detail-value">{accountData?.business_type || "--"}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Fax</span>
            <span className="detail-value">{accountData?.fax || "--"}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Website</span>
            <span className="detail-value link" style={{ color: "#3D4AF5" }}>
              {accountData?.website || "--"}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Employees</span>
            <span className="detail-value">{accountData?.employees || "0"} employees</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Modified By</span>
            <div className="detail-value">
              <div>{accountData?.modified_by || "--"}</div>
              <div style={{ fontWeight: "400", fontSize: "12px", color: "#626f86" }}>{accountData?.modified_at || "--"}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="view-card mt-3">
        <div className="view-card-title">Tax & Payment Info</div>
        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-label">GST Number</span>
            <span className="detail-value">{accountData?.gst_number || "--"}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Pan Number</span>
            <span className="detail-value">{accountData?.pan_number || "--"}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Payment Due Days</span>
            <span className="detail-value">{accountData?.credit_days || "--"} days</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Credit Limit</span>
            <span className="detail-value">₹{accountData?.credit_limit || "0.00"}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Discount %</span>
            <span className="detail-value">{accountData?.discount_percentage || "0"}%</span>
          </div>
        </div>
      </div>

      <div className="view-card mt-3">
        {/* <div className="view-card-title">
          <span>Address Information</span>
          <button className="btn-add-item">Locate in Map</button>
        </div> */}
        <div className="address-section">
          <div className="address-box">
            <div className="address-icon-bg"><img src={LocationIcon} alt="location" /></div>
            <div>
              <div className="address-type">Billing Address</div>
              <p className="address-content">{accountData?.bill_address ? `${accountData.bill_address.address}, ${accountData.bill_address.city}, ${accountData.bill_address.state}, ${accountData.bill_address.country} ${accountData.bill_address.zipcode}` : "--"}</p>
            </div>
          </div>
          <div className="address-box">
            <div className="address-icon-bg"><img src={LocationIcon} alt="location" /></div>
            <div>
              <div className="address-type">Shipping Address</div>
              <p className="address-content">{accountData?.ship_address ? `${accountData.ship_address.address}, ${accountData.ship_address.city}, ${accountData.ship_address.state}, ${accountData.ship_address.country} ${accountData.ship_address.zipcode}` : "--"}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderNotes = () => (
    <div className="view-card">
      <div className="view-card-title">Notes</div>
      <div style={{ maxHeight: "300px", overflowY: "auto", marginBottom: "24px" }}>
        {notes.length === 0 && <p style={{ textAlign: "center", color: "#8790A1", padding: "20px" }}>No notes yet.</p>}
        {notes.map((noteItem, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "flex-start",
              marginBottom: "20px",
            }}
          >
            <img
              src={Notesprofile}
              alt="Icon"
              style={{
                height: "36px",
                width: "36px",
                borderRadius: "50%",
                marginRight: "12px",
                flexShrink: 0,
              }}
            />
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <p style={{ margin: "0 0 4px 0", fontSize: "14px", color: "#172B4D", lineHeight: "1.4" }}>{noteItem.note}</p>
              <p
                style={{
                  margin: 0,
                  fontSize: "12px",
                  color: "#626f86",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <span style={{ fontWeight: "500", color: "#3d4af5" }}>Account</span>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <img src={Clock} alt="date" style={{ height: "14px", width: "14px" }} />
                  {noteItem.date}
                </span>
                <span>By {noteItem.created_by}</span>
              </p>
            </div>
          </div>
        ))}
      </div>
      <div className="add-note-wrapper">
        <div className="note-avatar-box">
          <img src={Notesprofile} alt="Avatar" />
        </div>
        <div className="note-input-container">
          <textarea 
            placeholder="Add a note..." 
            className="note-textarea" 
            value={newNote} 
            onChange={(e) => setNewNote(e.target.value)}
          />
          <div className="note-footer">
            <button className="note-btn cancel" onClick={() => setNewNote("")}>Cancel</button>
            <button className="note-btn add" onClick={handleAddNote}>Add Note</button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAttachments = () => (
    <div className="view-card">
      <div className="view-card-title">
        <span>Attachments</span>
        <button className="btn-add-item" onClick={handleAddAttachment}>
          <FaPlus size={12} /> Add attach
        </button>
        <input type="file" ref={fileInputRef} style={{ display: "none" }} accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx" onChange={handleFileChange} />
      </div>
      <table className="custom-table">
        <thead>
          <tr>
            <th>File Name</th>
            <th>Attached By</th>
            <th>Date Added</th>
            <th>Size</th>
          </tr>
        </thead>
        <tbody>
          {attachments.length === 0 && (
            <tr>
              <td colSpan="4" style={{ textAlign: "center", color: "#8790A1" }}>
                No attachments yet.
              </td>
            </tr>
          )}
          {attachments.map((attachItem, index) => (
            <tr key={index}>
              <td style={{ color: "#3D4AF5", fontWeight: "500" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                  <span style={{ maxWidth: "400px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={attachItem.file_name}>
                    {attachItem.file_name}
                  </span>
                  <FaDownload 
                    style={{ cursor: "pointer", color: "#626f86", fontSize: "14px" }} 
                    onClick={() => handleDownload(attachItem.url, attachItem.file_name)}
                    title="Download"
                  />
                </div>
              </td>
              <td>{attachItem.created_by}</td>
              <td>{attachItem.date}</td>
              <td>{attachItem.file_size} MB</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const handleOpenMailDetail = async (mailId) => {
    setSelectedMail({ id: mailId });
    setMailDetailLoading(true);
    try {
      const response = await getAccountMailDetailApi(accountId, mailId);
      if (response?.data?.success) {
        setSelectedMail(response.data.mail);
      }
    } catch (error) {
      console.error("Error fetching mail detail:", error);
    } finally {
      setMailDetailLoading(false);
    }
  };

  const renderDiscussion = () => (
    <div className="view-card">
      <div className="view-card-title">
        <span>Discussion</span>
      </div>
      <table className="custom-table">
        <thead>
          <tr>
            <th>Subject</th>
            <th>To</th>
            <th>Status</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {discussionMails.length === 0 && (
            <tr>
              <td colSpan="4" style={{ textAlign: "center", color: "#8790A1" }}>
                No mail sent for this account yet.
              </td>
            </tr>
          )}
          {discussionMails.map((mail) => (
            <tr key={mail.id} style={{ cursor: "pointer" }} onClick={() => handleOpenMailDetail(mail.id)}>
              <td style={{ color: "#3D4AF5", fontWeight: "500" }}>
                <span style={{ maxWidth: "400px", display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={mail.subject}>
                  {mail.subject}
                </span>
              </td>
              <td>{mail.receipent}</td>
              <td>
                <span className={`status-pill ${mail.status?.toLowerCase()}`}>{mail.status}</span>
              </td>
              <td>{mail.date}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {selectedMail && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={() => setSelectedMail(null)}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              width: "900px",
              maxWidth: "92vw",
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 24px",
                borderBottom: "1px solid #ebebeb",
              }}
            >
              <div className="fs-16 fw-600 text-dkblue">Mail Details</div>
              <span style={{ cursor: "pointer", fontSize: "18px", color: "#8790A1" }} onClick={() => setSelectedMail(null)}>
                ✕
              </span>
            </div>

            {mailDetailLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
                <Loader height="200px" />
              </div>
            ) : (
              <div style={{ padding: "24px", overflowY: "auto" }}>
                <div style={{ marginBottom: "12px" }}>
                  <div className="fs-12 fw-500" style={{ color: "#8790A1" }}>Subject</div>
                  <div className="fs-14 fw-600 text-dkblue">{selectedMail.subject}</div>
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <div className="fs-12 fw-500" style={{ color: "#8790A1" }}>To</div>
                  <div className="fs-14" style={{ color: "#172b4d" }}>{selectedMail.receipent}</div>
                </div>
                {selectedMail.cc && (
                  <div style={{ marginBottom: "12px" }}>
                    <div className="fs-12 fw-500" style={{ color: "#8790A1" }}>Cc</div>
                    <div className="fs-14" style={{ color: "#172b4d" }}>{selectedMail.cc}</div>
                  </div>
                )}
                {selectedMail.bcc && (
                  <div style={{ marginBottom: "12px" }}>
                    <div className="fs-12 fw-500" style={{ color: "#8790A1" }}>Bcc</div>
                    <div className="fs-14" style={{ color: "#172b4d" }}>{selectedMail.bcc}</div>
                  </div>
                )}
                <div style={{ marginBottom: "16px" }}>
                  <div className="fs-12 fw-500" style={{ color: "#8790A1" }}>Status</div>
                  <span className={`status-pill ${selectedMail.status?.toLowerCase()}`}>{selectedMail.status}</span>
                </div>

                {selectedMail.attachments?.length > 0 && (
                  <div style={{ marginBottom: "16px" }}>
                    <div className="fs-12 fw-500" style={{ color: "#8790A1", marginBottom: "8px" }}>Attachments</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {selectedMail.attachments.map((att) => (
                        <a
                          key={att.id}
                          href={att.file_path}
                          target="_blank"
                          rel="noreferrer"
                          className="fs-13 fw-500"
                          style={{ color: "#3D4AF5", textDecoration: "none" }}
                        >
                          {att.file_name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ borderTop: "1px solid #ebebeb", paddingTop: "16px" }}>
                  <div className="fs-12 fw-500" style={{ color: "#8790A1", marginBottom: "8px" }}>Content</div>
                  <div
                    style={{ fontSize: "14px", color: "#172b4d", maxHeight: "300px", overflowY: "auto", border: "1px solid #ebebeb", borderRadius: "8px", padding: "12px" }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedMail.content || "") }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderContacts = () => (
    <div className="view-card">
      <div className="view-card-title">
        <span>Contacts - {pagination["Contacts"].totalRecords}</span>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              borderRadius: "100px",
              border: "0.8px solid #e5e5e5",
              padding: "8px 14px",
              backgroundColor: "#ffffff",
              height: "36px",
            }}
          >
            {tabLoading["Contacts"] ? (
              <Spinner animation="border" size="sm" style={{ width: "14px", height: "14px" }} />
            ) : (
              <img src={Search} alt="search" style={{ width: "14px" }} />
            )}
            <input
              type="search"
              placeholder="Search contacts..."
              value={contactSearch}
              onChange={(e) => {
                setContactSearch(e.target.value);
                setTabPageOffset("Contacts", 0);
              }}
              className="no-focus-border"
              style={{ border: "none", outline: "none", fontSize: "13px", background: "transparent" }}
            />
          </div>
          <button className="btn-add-item" onClick={() => setShowCreateContact(true)}>
            <FaPlus size={12} /> Add contact
          </button>
        </div>
      </div>
      <table className="custom-table">
        <thead>
          <tr>
            <th style={{ width: "40px" }}>S.No</th>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            {hasDeletePermission && <th style={{ width: "60px" }}>Action</th>}
          </tr>
        </thead>
        <tbody>
          {contacts.length === 0 && (
            <tr>
              <td colSpan={hasDeletePermission ? 5 : 4} style={{ textAlign: "center", color: "#8790A1" }}>
                {contactSearch ? "No contacts match your search." : "No contacts yet."}
              </td>
            </tr>
          )}
          {contacts.map((contact, index) => (
            <tr key={index}>
              <td>{pagination["Contacts"].offset * pagination["Contacts"].itemsPerPage + index + 1}</td>
              <td style={{ color: "#3D4AF5", fontWeight: "500" }}>{contact.name}</td>
              <td>{contact.email}</td>
              <td>{contact.phone}</td>
              {hasDeletePermission && (
                <td>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteContact(contact.id);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "0.6px solid #E5E5E5",
                      cursor: "pointer",
                      justifyContent: "center",
                      width: "32px",
                      height: "32px",
                      borderRadius: "100px",
                      background: "#FFFFFF",
                    }}
                    title="Delete Contact"
                  >
                    <DeleteRow />
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <PaginationControls
        state={pagination["Contacts"]}
        onItemsPerPageChange={(n) => setTabItemsPerPage("Contacts", n)}
        onPageChange={(page) => setTabPageOffset("Contacts", page)}
      />
    </div>
  );

  const renderActivities = () => (
    <div className="details-card">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="card-title" style={{ marginBottom: 0 }}>
          Activities
        </div>
        <button
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            backgroundColor: "#FFFFFF",
            borderRadius: "40px",
            padding: "8px 18px",
            border: "1px solid #EBEBEB",
            cursor: "pointer",
            fontSize: "14px",
            height: "32px",
            color: "#3D4AF5",
            fontWeight: "600",
          }}
        >
          <FaPlus size={12} color="#3D4AF5" /> Add activity
        </button>
      </div>
      {activities.length === 0 && <p style={{ textAlign: "center", color: "#8790A1" }}>No activities yet.</p>}
      {activities.map((activity, index) => (
        <div
          key={index}
          style={{
            background: "#F8F9FC",
            borderRadius: "16px",
            padding: "18px",
            marginBottom: "12px",
            border: "1px solid #EBEBEB",
          }}
        >
          <div style={{ fontWeight: "600", fontSize: "15px", color: "#3D4AF5" }}>
            {activity.type} with {activity.contact}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "13px",
              marginTop: "10px",
              color: "#172B4D",
            }}
          >
            <span>{activity.date}</span>
          </div>
          <div style={{ fontSize: "13px", color: "#8790A1", marginTop: "4px" }}>{activity.description}</div>
        </div>
      ))}
    </div>
  );

  const renderHistory = () => (
    <div className="details-card">
      <div className="card-title">History</div>
      <div style={{ overflowX: "auto" }}>
        {history.length === 0 && <p style={{ textAlign: "center", color: "#8790A1" }}>No history yet.</p>}
        {history.map((historyItem, index) => (
          <div key={index} className="d-flex align-items-start mb-3">
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "start",
                marginRight: "1rem",
              }}
            >
              <div className="small text-muted" style={{ marginRight: "1rem" }}>
                {historyItem.time}
              </div>
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  borderRadius: "50%",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                •
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <p className="mb-1">
                <strong
                  style={{
                    fontWeight: 500,
                    fontSize: "16px",
                    color: "#21272C",
                  }}
                >
                  {historyItem.type}
                </strong>
                <span> - {historyItem.description}</span>
              </p>
              <div className="small text-muted">
                By {historyItem.first_name} on {historyItem.date}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderQuotation = () => (
    <div className="view-card">
      <div className="view-card-title">
        <span>Quotations - {pagination["Quotation"].totalRecords}</span>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              borderRadius: "100px",
              border: "0.8px solid #e5e5e5",
              padding: "8px 14px",
              backgroundColor: "#ffffff",
              height: "36px",
            }}
          >
            {tabLoading["Quotation"] ? (
              <Spinner animation="border" size="sm" style={{ width: "14px", height: "14px" }} />
            ) : (
              <img src={Search} alt="search" style={{ width: "14px" }} />
            )}
            <input
              type="search"
              placeholder="Search quotations..."
              value={quotationSearch}
              onChange={(e) => {
                setQuotationSearch(e.target.value);
                setTabPageOffset("Quotation", 0);
              }}
              className="no-focus-border"
              style={{ border: "none", outline: "none", fontSize: "13px", background: "transparent" }}
            />
          </div>
          <button
            className="btn-add-item"
            onClick={() =>
              navigate("/quotation/create", {
                state: {
                  callFrom: "Customer",
                  moduleDetail: { value: accountId, label: accountData?.first_name },
                },
              })
            }
          >
            <FaPlus size={12} /> Add
          </button>
        </div>
      </div>
      <table className="custom-table">
        <thead>
          <tr>
            <th style={{ width: "40px" }}>S.No</th>
            <th>Quotation No.</th>
            {/* <th>Customer</th> */}
            <th>Contact Person</th>
            <th>Validity Date</th>
            <th>Amount</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {quotations.length === 0 && (
            <tr>
              <td colSpan="7" style={{ textAlign: "center", color: "#8790A1" }}>
                {quotationSearch ? "No quotations match your search." : "No quotations yet."}
              </td>
            </tr>
          )}
          {quotations.map((quotation, index) => (
            <tr key={index}>
              <td>{pagination["Quotation"].offset * pagination["Quotation"].itemsPerPage + index + 1}</td>
              <td style={{ color: "#172B4D", fontWeight: "500" }}>{quotation.quotation_no}</td>
             {/*  <td>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      backgroundColor: "#1E3A5F",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "11px",
                      fontWeight: "600",
                    }}
                  >
                    {quotation.customer.charAt(0)}
                  </div>
                  <span>{quotation.customer}</span>
                </div>
              </td> */}
              <td>{quotation.contact_person}</td>
              <td style={{ color: "#3D4AF5" }}>{quotation.validity_date}</td>
              <td>₹{quotation.amount}</td>
              <td>
                <span className={`status-pill ${quotation.status?.toLowerCase()}`}>{quotation.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <PaginationControls
        state={pagination["Quotation"]}
        onItemsPerPageChange={(n) => setTabItemsPerPage("Quotation", n)}
        onPageChange={(page) => setTabPageOffset("Quotation", page)}
      />
    </div>
  );

  const renderInvoice = () => (
    <div className="view-card">
      <div className="view-card-title">
        <span>Invoices - {pagination["Invoice"].totalRecords}</span>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              borderRadius: "100px",
              border: "0.8px solid #e5e5e5",
              padding: "8px 14px",
              backgroundColor: "#ffffff",
              height: "36px",
            }}
          >
            {tabLoading["Invoice"] ? (
              <Spinner animation="border" size="sm" style={{ width: "14px", height: "14px" }} />
            ) : (
              <img src={Search} alt="search" style={{ width: "14px" }} />
            )}
            <input
              type="search"
              placeholder="Search invoices..."
              value={invoiceSearch}
              onChange={(e) => {
                setInvoiceSearch(e.target.value);
                setTabPageOffset("Invoice", 0);
              }}
              className="no-focus-border"
              style={{ border: "none", outline: "none", fontSize: "13px", background: "transparent" }}
            />
          </div>
          <button className="btn-add-item" onClick={() => navigate("/invoice/create", { state: { accountId, accountName: accountData?.first_name } })}>
            <FaPlus size={12} /> Add
          </button>
        </div>
      </div>
      <table className="custom-table">
        <thead>
          <tr>
            <th style={{ width: "40px" }}>S.No</th>
            <th>Invoice No.</th>
            {/* <th>Customer</th> */}
            <th>Sales ID</th>
            <th>Due Date</th>
            <th>Amount</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {invoices.length === 0 && (
            <tr>
              <td colSpan="7" style={{ textAlign: "center", color: "#8790A1" }}>
                {invoiceSearch ? "No invoices match your search." : "No invoices yet."}
              </td>
            </tr>
          )}
          {invoices.map((invoice, index) => (
            <tr key={index}>
              <td>{pagination["Invoice"].offset * pagination["Invoice"].itemsPerPage + index + 1}</td>
              <td style={{ color: "#172B4D", fontWeight: "500" }}>{invoice.order_no}</td>
              {/* <td>{invoice.customer_name}</td> */}
              <td>{invoice.sales_id}</td>
              <td>{invoice.due_date}</td>
              <td>₹{invoice.amount}</td>
              <td>
                <span className={`status-pill ${invoice.status?.toLowerCase()}`}>{invoice.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <PaginationControls
        state={pagination["Invoice"]}
        onItemsPerPageChange={(n) => setTabItemsPerPage("Invoice", n)}
        onPageChange={(page) => setTabPageOffset("Invoice", page)}
      />
    </div>
  );

  const renderPaymentHistory = () => (
    <div className="view-card">
      <div className="view-card-title">
        <span>Payments History - {pagination["Payment History"].totalRecords}</span>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              borderRadius: "100px",
              border: "0.8px solid #e5e5e5",
              padding: "8px 14px",
              backgroundColor: "#ffffff",
              height: "36px",
            }}
          >
            {tabLoading["Payment History"] ? (
              <Spinner animation="border" size="sm" style={{ width: "14px", height: "14px" }} />
            ) : (
              <img src={Search} alt="search" style={{ width: "14px" }} />
            )}
            <input
              type="search"
              placeholder="Search payments..."
              value={paymentSearch}
              onChange={(e) => {
                setPaymentSearch(e.target.value);
                setTabPageOffset("Payment History", 0);
              }}
              className="no-focus-border"
              style={{ border: "none", outline: "none", fontSize: "13px", background: "transparent" }}
            />
          </div>
          <button className="btn-add-item" onClick={() => navigate("/payment/create", { state: { accountId, accountName: accountData?.first_name } })}>
            <FaPlus size={12} /> Add
          </button>
        </div>
      </div>
      <table className="custom-table">
        <thead>
          <tr>
            <th style={{ width: "40px" }}>S.No</th>
            <th>Payment No.</th>
            {/* <th>Customer</th> */}
            <th>Invoice</th>
            <th>Date</th>
            <th>Amount</th>
            <th>Paid</th>

            <th>Outstanding</th>
            <th>Currency Rate</th>
            <th>Created By</th>
            <th>Created Date</th>
          </tr>
        </thead>
        <tbody>
          {payments.length === 0 && (
            <tr>
              <td colSpan="10" style={{ textAlign: "center", color: "#8790A1" }}>
                {paymentSearch ? "No payments match your search." : "No payment history yet."}
              </td>
            </tr>
          )}
          {payments.map((payment, index) => (
            <tr key={index}>
              <td>{pagination["Payment History"].offset * pagination["Payment History"].itemsPerPage + index + 1}</td>
              <td style={{ color: "#172B4D", fontWeight: "500" }}>{payment.payment_id}</td>
              {/* <td>{payment.customer_name}</td> */}
              <td>{payment.invoice}</td>
              <td>{payment.date}</td>
              <td>₹{payment.amount}</td>
              <td className="green">₹{payment.paid}</td>

              <td className="red">₹{payment.outstanding}</td>
              <td>{payment.currency_rate || "—"}</td>
              <td>{payment.created_by}</td>
              <td>{payment.created_date}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <PaginationControls
        state={pagination["Payment History"]}
        onItemsPerPageChange={(n) => setTabItemsPerPage("Payment History", n)}
        onPageChange={(page) => setTabPageOffset("Payment History", page)}
      />
    </div>
  );

  const renderTransactionHistory = () => (
    <div className="view-card">
      <div className="view-card-title">
        <span>Transaction History - {pagination["Transaction History"].totalRecords}</span>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            borderRadius: "100px",
            border: "0.8px solid #e5e5e5",
            padding: "8px 14px",
            backgroundColor: "#ffffff",
            height: "36px",
          }}
        >
          {tabLoading["Transaction History"] ? (
            <Spinner animation="border" size="sm" style={{ width: "14px", height: "14px" }} />
          ) : (
            <img src={Search} alt="search" style={{ width: "14px" }} />
          )}
          <input
            type="search"
            placeholder="Search transactions..."
            value={transactionSearch}
            onChange={(e) => {
              setTransactionSearch(e.target.value);
              setTabPageOffset("Transaction History", 0);
            }}
            className="no-focus-border"
            style={{ border: "none", outline: "none", fontSize: "13px", background: "transparent" }}
          />
        </div>
      </div>
      <table className="custom-table">
        <thead>
          <tr>
            <th style={{ width: "40px" }}>S.No</th>
            <th>Payment No.</th>
            {/* <th>Customer</th> */}
            <th>Payment Amount</th>
            <th>Unallocated</th>
            <th>Payment Mode</th>
            <th>Created By</th>
            <th>Created Date</th>
          </tr>
        </thead>
        <tbody>
          {transactions.length === 0 && (
            <tr>
              <td colSpan="8" style={{ textAlign: "center", color: "#8790A1" }}>
                {transactionSearch ? "No transactions match your search." : "No transaction history yet."}
              </td>
            </tr>
          )}
          {transactions.map((txn, index) => (
            <tr key={index}>
              <td>{pagination["Transaction History"].offset * pagination["Transaction History"].itemsPerPage + index + 1}</td>
              <td style={{ color: "#172B4D", fontWeight: "500" }}>{txn.payment_id}</td>
              {/* <td>{txn.customer_name}</td> */}
              <td className="green">₹{txn.amount}</td>
              <td>₹{txn.unallocated || "0.00"}</td>
              <td>{txn.payment_mode || "--"}</td>
              <td>{txn.created_by}</td>
              <td>{txn.created_date}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <PaginationControls
        state={pagination["Transaction History"]}
        onItemsPerPageChange={(n) => setTabItemsPerPage("Transaction History", n)}
        onPageChange={(page) => setTabPageOffset("Transaction History", page)}
      />
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "Account Details":
        return renderAccountDetails();
      case "AI Summary":
        return (
          <AiSummaryCard
            api={getAccountAiSummaryApi}
            module="account"
            id={accountId}
            initial={accountData}
            hint="Score this account on its trading history — quotations, orders and unpaid invoices — with a suggested next action."
          />
        );
      case "Notes":
        return renderNotes();
      case "Attachments":
        return renderAttachments();
      case "Discussion":
        return renderDiscussion();
      case "Contacts":
        return renderContacts();
      case "Activities":
        return renderActivities();
      case "History":
        return renderHistory();
      case "Quotation":
        return renderQuotation();
      case "Invoice":
        return renderInvoice();
      case "Payment History":
        return renderPaymentHistory();
      case "Transaction History":
        return renderTransactionHistory();
      default:
        return renderAccountDetails();
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Loader height="100vh" />
      </div>
    );
  }

  return (
    <div className="account-view-page">
      {/* Header Section */}
      <div className="view-header">
        <div className="view-header-left">
          <div className="view-back-btn" onClick={() => navigate(-1)}>
            <img src={Back} alt="Back" />
          </div>
          <div className="view-avatar" style={{ backgroundColor: "#A81D21" }}>
            {accountData?.profile_image ? <img src={accountData.profile_image} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ color: "#fff", fontSize: "24px", fontWeight: "bold" }}>A</span>}
          </div>
          <div className="view-header-info">
            <div className="view-account-name" style={{ fontSize: "16px", fontWeight: "700" }}>
              {accountData?.first_name || "--"}
            </div>
            <a className="view-account-website" href={accountData?.website || "#"} target="_blank" rel="noreferrer" style={{ fontSize: "13px" }}>
              {accountData?.website || "--"}
            </a>
          </div>
        </div>
        <div className="view-header-right">
          <div style={{ display: "flex", gap: "12px", alignItems: "center",justifyContent: "flex-end" }}>
          <button
            className="btn-action primary"
            style={{ backgroundColor: "#6B46FE", borderRadius: "100px", padding: "8px 24px" }}
            onClick={handleOpenMailModal}
          >
            Send mail
          </button>
          {Number(accountData?.outstanding_amount) > 1 && (
            <button
              className="btn-action"
              style={{ backgroundColor: "#6B46FE", color: "#fff", borderRadius: "100px", padding: "8px 24px", border: "none" }}
              onClick={() => {
                setInitialReportModalTab("Preview");
                setShowOutstandingReport(true);
              }}
            >
              Outstanding
            </button>
          )}
          {hasCreateCallPermission && (
            <button
              className="btn-action"
              style={{ backgroundColor: "#ffffff", color: "#6B46FE", borderRadius: "100px", padding: "8px 24px", border: "1px solid #6B46FE" }}
              onClick={() => setShowCreateCall(true)}
            >
              Create Call
            </button>
          )}
          <div style={{ position: "relative" }}>
            <div
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 16px",
                border: "1px solid #D4DDE7",
                borderRadius: "100px",
                fontSize: "14px",
                fontWeight: "600",
                color: "#172B4D",
                cursor: "pointer",
                backgroundColor: "#fff",
              }}
            >
              <span style={{ 
                width: "12px", 
                height: "12px", 
                borderRadius: "50%", 
                backgroundColor: Number(accountData?.status) === 1 ? "#1aa56a" : "#d93025" 
              }}></span>
              {Number(accountData?.status) === 1 ? "Active" : "Inactive"}
              <span style={{ fontSize: "10px", marginLeft: "4px" }}>▼</span>
            </div>

            {showStatusDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "45px",
                  left: "0",
                  backgroundColor: "#fff",
                  border: "1px solid #D4DDE7",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                  zIndex: 10,
                  width: "140px",
                  overflow: "hidden"
                }}
              >
                <div 
                  className="status-option"
                  onClick={() => handleStatusUpdate(true)}
                  style={{ padding: "10px 16px", cursor: "pointer", fontSize: "14px", display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#1aa56a" }}></span>
                  Active
                </div>
                <div 
                  className="status-option"
                  onClick={() => handleStatusUpdate(false)}
                  style={{ padding: "10px 16px", cursor: "pointer", fontSize: "14px", display: "flex", alignItems: "center", gap: "8px", borderTop: "1px solid #f0f0f0" }}
                >
                  <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#d93025" }}></span>
                  Inactive
                </div>
              </div>
            )}
          </div>
          <div style={{ position: "relative" }}>
            <div
              onClick={() => setShowKebabMenu(!showKebabMenu)}
              style={{
                width: "36px",
                height: "36px",
                border: "1px solid #D4DDE7",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              ⋮
            </div>

            {showKebabMenu && (
              <div
                style={{
                  position: "absolute",
                  top: "45px",
                  right: "0",
                  backgroundColor: "#fff",
                  border: "1px solid #D4DDE7",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                  zIndex: 10,
                  width: "140px",
                  overflow: "hidden"
                }}
              >
                <div 
                  onClick={() => {
                    setShowKebabMenu(false);
                    navigate("/accounts/create", { state: { id: accountId } });
                  }}
                  style={{ padding: "10px 16px", cursor: "pointer", fontSize: "14px", display: "flex", alignItems: "center", gap: "8px" }}
                >
                  Edit
                </div>
                {/* Could add Delete later if needed */}
              </div>
            )}
          </div>
        </div>
        
        <div className="view-header-stats mt-2">
          <div className="stat-item">
            <span className="stat-label">Invoiced</span>
            <span className="stat-value">₹{accountData?.invoiced_amount || "0.00"}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Paid</span>
            <span className="stat-value green" style={{ color: "#1aa56a" }}>
              ₹{accountData?.paid_amount || "0.00"}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">TDS</span>
            <span className="stat-value">₹{accountData?.tds_amount || "0.00"}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Tax</span>
            <span className="stat-value">₹{accountData?.tax_amount || "0.00"}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Outstanding</span>
            <span className="stat-value red" style={{ color: "#e53935" }}>
              ₹{accountData?.outstanding_amount || "0.00"}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Overdue</span>
            <span className="stat-value">₹{accountData?.overdue_amount || "0.00"}</span>
          </div>

          <div className="stat-item">
            <span className="stat-label">Cancelled</span>
            <span className="stat-value" style={{ color: "#e53935" }}>
              ₹{accountData?.cancelled_amount || "0.00"}
            </span>
          </div>

          <div className="stat-item">
            <span className="stat-label">Unallocated</span>
            <span className="stat-value" style={{ color: "#3D4AF5" }}>
              ₹{accountData?.unallocated_balance || "0.00"}
            </span>
          </div>
        </div>
</div>
        
      </div>

      <div className="view-body row">
        {/* Sidebar */}
        <div className="view-sidebar col-3">
          <div className="sidebar-card">
            <div className="sidebar-section">
              <div className="sidebar-title">Account details</div>
              <div className="info-item">
                <div className="info-icon-box">
                  <img src={PhoneIcon} alt="Phone" />
                </div>
                <div className="info-text-main">{accountData?.phone_number || "--"}</div>
              </div>
              <div className="info-item">
                <div className="info-icon-box">
                  <img src={MailIcon} alt="Email" />
                </div>
                <div className="info-text-main">{accountData?.email || "--"}</div>
              </div>
              <div className="info-item">
                <div className="info-icon-box">
                  <img src={UserIcon} alt="Owner" />
                </div>
                <div className="info-text-main">
                  {accountData?.account_owner || "--"}
                  <span className="owner-pill">Account owner</span>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon-box">
                  <img src={ITIcon} alt="Industry" />
                </div>
                <div className="info-text-main">{accountData?.industry || "--"}</div>
              </div>
              <div className="info-item">
                <div className="info-icon-box">
                  <img src={amountIcon} alt="Revenue" />
                </div>
                <div className="info-text-main">₹{accountData?.annual_revenue || "0.00"}</div>
              </div>
              <div className="info-item">
                <div className="info-icon-box">
                  <img src={LocationIcon} alt="Location" />
                </div>
                <div className="info-text-main">{accountData?.account_site || "--"}</div>
              </div>
            </div>

            <div className="sidebar-divider" />

            <div className="sidebar-section">
              <div className="sidebar-title">Contact</div>
              <div className="sidebar-contact-section">
                {sidebarContact ? (
                  <div className="contact-mini-card">
                    <div className="contact-mini-avatar">
                      <img src={Notesprofile} alt="Avatar" style={{ width: "100%" }} />
                    </div>
                    <div className="contact-mini-info">
                      <div className="contact-mini-name">{sidebarContact.name}</div>
                      <div className="contact-mini-detail">
                        <img src={PhoneIcon} alt="Phone" style={{ width: "12px" }} />
                        {sidebarContact.phone || sidebarContact.mobile || "--"}
                      </div>
                      <div className="contact-mini-detail">
                        <img src={MailIcon} alt="Mail" style={{ width: "12px" }} />
                        <span style={{ color: "#3d4af5" }}>{sidebarContact.email || "--"}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: "12px", color: "#626f86" }}>No primary contact found.</div>
                )}
              </div>
            </div>

            <div className="sidebar-divider" />

            <div className="sidebar-section">
              <div className="sidebar-title">Description</div>
              <p className="description-text" style={{ fontSize: "13px", color: "#626f86" }}>
                {accountData?.description || "No description available."}
              </p>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="view-main-content col-9">
          <div className="tabs-container" style={{ position: "relative", overflow: "hidden" }}>
            <div className="tab-nav-arrow" style={{ position: "absolute", left: "8px", zIndex: 2, cursor: "pointer" }} onClick={() => scrollTabs(-1)}>
              <img src={ArrowPrev} alt="arrow-prev" />
            </div>
            <div className="tab-list" ref={tabListRef} style={{ display: "flex", overflowX: "auto", padding: "0 40px", scrollbarWidth: "none" }}>
              {tabs.map((tab) => (
                <button key={tab} className={`tab-btn ${activeTab === tab ? "active" : ""}`} onClick={() => setActiveTab(tab)}>
                  {tab}
                </button>
              ))}
            </div>
            <div className="tab-nav-arrow" style={{ position: "absolute", right: "8px", zIndex: 2, cursor: "pointer" }} onClick={() => scrollTabs(1)}>
              <img src={ArrowNext} alt="arrow-next" />
            </div>
          </div>

          <div className="tab-content-area">{renderTabContent()}</div>
        </div>
      </div>
      {showMailModal && (
        <div className="mail-modal-overlay">
          <div className="mail-modal">
            <div className="mail-modal-header">
              <span>New Message</span>
              <div className="header-actions">
                <span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </span>
                <span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                </span>
                <span onClick={() => setShowMailModal(false)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </span>
              </div>
            </div>
            <div className="mail-modal-content">
              {mailError && (
                <div style={{ color: "red", fontSize: "12px", marginBottom: "10px", padding: "0 16px" }}>
                  {mailError}
                </div>
              )}
              <div className="input-row">
                <label>To</label>
                <input 
                  type="text" 
                  placeholder="Recipients" 
                  className="no-focus-border"
                  value={mailData.to_email}
                  onChange={(e) => setMailData({...mailData, to_email: e.target.value})}
                />
                <div className="cc-bcc">
                  <span onClick={() => setShowCc(!showCc)}>Cc</span>
                  <span onClick={() => setShowBcc(!showBcc)}>Bcc</span>
                </div>
              </div>
              {showCc && (
                <div className="input-row">
                  <label>Cc</label>
                  <div className="chip-input-wrapper">
                    {ccChips.map((chip, index) => (
                      <span key={index} className="chip">
                        {chip}
                        <span className="chip-close" onClick={() => setCcChips(ccChips.filter((_, i) => i !== index))}>×</span>
                      </span>
                    ))}
                    <input 
                      type="text" 
                      className="chip-real-input no-focus-border"
                      placeholder={ccChips.length === 0 ? "Enter email..." : ""}
                      value={ccInput}
                      onChange={(e) => { setCcInput(e.target.value); setMailError(""); }}
                      style={hasCcInvalidInput ? { borderBottom: "1px solid red" } : {}}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === "," || e.key === " ") {
                          e.preventDefault();
                          const val = ccInput.trim().replace(",", "");
                          if (val && !ccChips.includes(val)) {
                            if (!isValidEmail(val)) {
                              setMailError(`Invalid CC email: ${val}`);
                              return;
                            }
                            setCcChips([...ccChips, val]);
                            setCcInput("");
                            setMailError("");
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              )}
              {showBcc && (
                <div className="input-row">
                  <label>Bcc</label>
                  <div className="chip-input-wrapper">
                    {bccChips.map((chip, index) => (
                      <span key={index} className="chip">
                        {chip}
                        <span className="chip-close" onClick={() => setBccChips(bccChips.filter((_, i) => i !== index))}>×</span>
                      </span>
                    ))}
                    <input 
                      type="text" 
                      className="chip-real-input no-focus-border"
                      placeholder={bccChips.length === 0 ? "Enter email..." : ""}
                      value={bccInput}
                      onChange={(e) => { setBccInput(e.target.value); setMailError(""); }}
                      style={hasBccInvalidInput ? { borderBottom: "1px solid red" } : {}}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === "," || e.key === " ") {
                          e.preventDefault();
                          const val = bccInput.trim().replace(",", "");
                          if (val && !bccChips.includes(val)) {
                            if (!isValidEmail(val)) {
                              setMailError(`Invalid BCC email: ${val}`);
                              return;
                            }
                            setBccChips([...bccChips, val]);
                            setBccInput("");
                            setMailError("");
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="input-row">
                <label>Subject</label>
                <input 
                  type="text" 
                  placeholder="Subject" 
                  className="no-focus-border"
                  value={mailData.subject}
                  onChange={(e) => setMailData({...mailData, subject: e.target.value})}
                />
              </div>
              <div className="body-content">
                {isFormatting ? (
                  <div className="quill-wrapper">
                    <ReactQuill 
                      theme="snow" 
                      value={mailData.body} 
                      onChange={(content) => setMailData({...mailData, body: content})}
                      style={{ height: '300px', marginBottom: '40px' }}
                    />
                  </div>
                ) : (
                  <textarea 
                    placeholder="Body Text"
                    className="no-focus-border"
                    value={mailData.body}
                    onChange={(e) => setMailData({...mailData, body: e.target.value})}
                  ></textarea>
                )}
              </div>
              {selectedFiles.length > 0 && (
                <div className="attached-files-container" style={{ padding: "0 16px 12px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {selectedFiles.map((file, index) => (
                    <div key={index} style={{ display: "flex", alignItems: "center", gap: "6px", backgroundColor: "#f1f3f4", padding: "4px 10px", borderRadius: "4px", fontSize: "12px", border: "1px solid #dadce0" }}>
                      <img src={AttachIcon} alt="file" style={{ width: "12px" }} />
                      <span style={{ color: "#3c4043", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                      <span style={{ cursor: "pointer", fontWeight: "bold", padding: "0 2px" }} onClick={() => setSelectedFiles(selectedFiles.filter((_, i) => i !== index))}>×</span>
                    </div>
                  ))}
                </div>
              )}
              {showDriveInput && (
                <div style={{ padding: "0 16px 12px" }}>
                  <input 
                    type="text" 
                    placeholder="Paste Drive link here..." 
                    className="no-focus-border"
                    value={driveLink}
                    onChange={(e) => setDriveLink(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setMailData({...mailData, body: mailData.body + `\nDrive Link: ${driveLink}\n`});
                        setDriveLink("");
                        setShowDriveInput(false);
                      }
                    }}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: "4px", border: "1px solid #dadce0", fontSize: "13px" }}
                  />
                </div>
              )}
              {showEmojiPicker && (
                <div style={{ padding: "0 16px 12px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  {["😊", "👍", "🙌", "📍", "✉️", "📎", "🚀", "💡", "✅"].map((emoji) => (
                    <span 
                      key={emoji} 
                      style={{ cursor: "pointer", fontSize: "20px" }}
                      onClick={() => {
                        setMailData({...mailData, body: mailData.body + emoji});
                        setShowEmojiPicker(false);
                      }}
                    >
                      {emoji}
                    </span>
                  ))}
                </div>
              )}
              {showLinkInput && (
                <div style={{ padding: "0 16px 12px", display: "flex", gap: "8px", alignItems: "center" }}>
                  <input 
                    type="text" 
                    placeholder="Display text" 
                    className="no-focus-border"
                    value={linkDisplay}
                    onChange={(e) => setLinkDisplay(e.target.value)}
                    style={{ flex: 1, padding: "8px 12px", borderRadius: "4px", border: "1px solid #dadce0", fontSize: "13px" }}
                  />
                  <input 
                    type="text" 
                    placeholder="Link URL" 
                    className="no-focus-border"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    style={{ flex: 2, padding: "8px 12px", borderRadius: "4px", border: "1px solid #dadce0", fontSize: "13px" }}
                  />
                  <button 
                    onClick={() => {
                      if (linkUrl) {
                        const formattedLink = linkDisplay ? `<a href="${linkUrl}">${linkDisplay}</a>` : linkUrl;
                        setMailData({...mailData, body: mailData.body + "\n" + formattedLink});
                        setLinkUrl("");
                        setLinkDisplay("");
                        setShowLinkInput(false);
                      }
                    }}
                    style={{ backgroundColor: "#6B46FE", color: "#fff", border: "none", padding: "8px 16px", borderRadius: "4px", fontSize: "13px", cursor: "pointer" }}
                  >
                    Insert
                  </button>
                  <span style={{ cursor: "pointer", fontSize: "18px", color: "#626f86" }} onClick={() => setShowLinkInput(false)}>×</span>
                </div>
              )}
            </div>
            <div className="mail-modal-footer">
              <div className="footer-left">
                <div className="icon-container" onClick={() => setIsFormatting(!isFormatting)} title="Formatting options">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isFormatting ? "#1a73e8" : "#5f6368"} strokeWidth="2"><path d="M4 20h16M7 16l5-9 5 9M8 12h8"/></svg>
                </div>
                <div className="icon-container" onClick={() => mailAttachmentRef.current.click()} title="Attach files">
                  <img src={AttachIcon} alt="attach" />
                  <input
                    type="file"
                    ref={mailAttachmentRef}
                    style={{ display: 'none' }}
                    multiple
                    onChange={(e) => {
                      if (e.target.files.length > 0) {
                        if (selectedFiles.length + e.target.files.length > 5) {
                          setMailError("Maximum of 5 attachments are allowed.");
                          e.target.value = "";
                          return;
                        }
                        setSelectedFiles([...selectedFiles, ...Array.from(e.target.files)]);
                      }
                      e.target.value = "";
                    }}
                  />
                </div>
                <div className="icon-container" onClick={() => setShowLinkInput(!showLinkInput)} title="Insert link">
                  <img src={LinkIcon} alt="link" />
                </div>
                {/* <div className="icon-container" onClick={() => setShowEmojiPicker(!showEmojiPicker)} title="Insert emoji">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={showEmojiPicker ? "#1a73e8" : "#5f6368"} strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
                </div>
                <div className="icon-container" onClick={() => setShowDriveInput(!showDriveInput)} title="Insert files using Drive">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={showDriveInput ? "#1a73e8" : "#5f6368"} strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
                </div>
                <div className="icon-container" title="Insert photo">
                  <img src={CameraIcon} alt="image" />
                </div>
                <div className="icon-container" title="Confidential mode">
                  <img src={ClockIcon} alt="schedule" />
                </div>
                <div className="icon-container" title="Signature functionality">
                  <img src={EditPenIcon} alt="pen" />
                </div> */}
              </div>
              <button className="send-btn" onClick={handleSendMail} disabled={mailLoading}>
                {mailLoading ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showOutstandingReport && (
        <OutstandingReportModal
          accountId={accountId}
          initialTab={initialReportModalTab}
          onClose={() => {
            setShowOutstandingReport(false);
            setInitialReportModalTab("Preview");
          }}
          notify={notify}
        />
      )}
      <CreateCallModal
        isOpen={showCreateCall}
        onClose={() => setShowCreateCall(false)}
        notify={notify}
        linkTo={{ type: "Account", id: accountId, label: accountData?.first_name }}
      />
      <CreateContactModal
        isOpen={showCreateContact}
        onClose={() => setShowCreateContact(false)}
        onCreated={handleContactCreated}
        notify={notify}
        linkTo={{ type: "Account", id: accountId, label: accountData?.first_name }}
      />
      {isOpenDeleteContact && (
        <DeleteModal
          deleteUrl={`account/${accountId}/remove_contact/${deleteContactId}`}
          onSuccess={onSuccessDeleteContact}
          onCancel={cancelDeleteContact}
        />
      )}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 3000 }}>
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
          bg={toastSuccess ? "success" : "danger"}
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default AccountView;
